/**
 * Auth abstraction over MSAL for token and account access.
 * See docs/AUTH_UPGRADE_README.md Part C & F.
 */

import type { AccountInfo } from '@azure/msal-browser';
import { getMsalInstance, loginRequestScopes } from 'src/auth/msalConfig';
import { getAuth, broadcastLogout } from 'src/app/functions/storage';
import { getUrlDetails } from 'src/app/functions/core';

let cachedAuthorization: string | null = null;

/** Legacy auth shape from getAuth() (storage) for fallback when MSAL cache not yet set. */
interface LegacyAuth {
  authorization?: string;
}

/**
 * Cache the current auth header (Bearer token) for sync consumers (map, layersResolver).
 * Called when we have a token from MSAL or legacy storage.
 */
export const setCachedAuthorization = (authorization: string): void => {
  cachedAuthorization = authorization?.startsWith('Bearer ') ? authorization : `Bearer ${authorization}`;
};

/**
 * Clear the in-memory auth header cache (e.g. on logout so no stale token is returned).
 */
export const clearCachedAuthorization = (): void => {
  cachedAuthorization = null;
};

/**
 * Sync auth header for non-async call sites (map.ts, layersResolver.ts).
 * Prefer cached value from MSAL path; fallback to legacy getAuth().authorization.
 */
export const getAuthorizationHeader = (): string => {
  if (cachedAuthorization) { return cachedAuthorization; }
  const auth = getAuth() as LegacyAuth | null;
  return auth?.authorization ?? '';
};

/**
 * Returns the current access token (silent refresh if needed).
 * Rejects if no account or token acquisition fails (e.g. re-login required).
 * Updates the sync cache so getAuthorizationHeader() stays in sync.
 */
export const getAccessToken = async (): Promise<string> => {
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();
  if (!accounts.length) {
    throw new Error('No account');
  }
  const result = await instance.acquireTokenSilent({
    scopes: loginRequestScopes,
    account: accounts[0],
  });
  setCachedAuthorization(`Bearer ${result.accessToken}`);
  return result.accessToken;
};

/**
 * Returns the first MSAL account if any (for Heap, Raygun, etc.).
 */
export const getAccount = (): AccountInfo | null => {
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
};

/**
 * Builds the post-logout redirect URI including the site name (e.g. origin/asa.bne/logout).
 * Used so the app lands on /{siteName}/logout after B2C logout.
 */
export const getPostLogoutRedirectUri = (): string => {
  const { siteName } = getUrlDetails();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (siteName && siteName !== 'logout') {
    return `${origin}/${siteName}/logout`;
  }
  return `${origin}/logout`;
};

/**
 * Redirects to B2C logout (and then to postLogoutRedirectUri including site name, e.g. /asa.bne/logout).
 * Broadcasts to other tabs so they clear MSAL cache and reload.
 */
export const logout = (): void => {
  clearCachedAuthorization();
  broadcastLogout();
  void getMsalInstance().logoutRedirect({
    postLogoutRedirectUri: getPostLogoutRedirectUri(),
  });
};

/**
 * Auth data shape for consumers that expect legacy getAuth() format (e.g. Heap).
 * Use when you have the token string (e.g. from Authenticated) to avoid async in render.
 */
export const getAuthDataFromToken = (token: string): {
  authorization: string;
  isAnonymous: boolean;
} => {
  const authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  return { authorization, isAnonymous: false };
};

/**
 * Removes the cached access token from MSAL storage so the next acquireTokenSilent
 * uses the refresh token. Call on 401 before retrying so a new token is fetched.
 */
export const clearAccessTokenCache = (): void => {
  clearCachedAuthorization();
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (!key?.toLowerCase().includes('accesstoken')) { continue; }
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) { continue; }
      const entry = JSON.parse(raw) as { credentialType?: string };
      if (entry?.credentialType === 'AccessToken') {
        sessionStorage.removeItem(key);
      }
    } catch {
      // ignore parse errors
    }
  }
}